import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ownerLinks } from "@/components/layout/sidebar/navigationLinks";
import { supabase } from "@/integrations/supabase/client";
import { useBusinessTypeFeatures } from "@/hooks/useBusinessTypeFeatures";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

interface FeatureAccessListProps {
  onFeatureClick: (feature: string) => void;
}

export const FeatureAccessList = ({ onFeatureClick }: FeatureAccessListProps) => {
  const [accessCodes, setAccessCodes] = useState<Record<string, string>>({});
  const [enabledFeatures, setEnabledFeatures] = useState<Record<string, boolean>>({});
  const { businessTypeFeatures, hasFeatureAccess } = useBusinessTypeFeatures();

  useEffect(() => {
    fetchAccessControls();
  }, []);

  const fetchAccessControls = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        toast.error("Vous devez être connecté");
        return;
      }

      const { data, error } = await supabase
        .from('access_controls')
        .select('*')
        .eq('owner_id', session.user.id);

      if (error) throw error;
      
      if (data) {
        const codes = data.reduce((acc, item) => ({
          ...acc,
          [item.feature_name]: item.access_code
        }), {});
        setAccessCodes(codes);
        
        const enabled = data.reduce((acc, item) => ({
          ...acc,
          [item.feature_name]: !item.is_disabled
        }), {});
        setEnabledFeatures(enabled);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des contrôles d'accès:", error);
    }
  };

  const handleToggleFeature = async (feature: string) => {
    const newState = !enabledFeatures[feature];
    setEnabledFeatures(prev => ({ ...prev, [feature]: newState }));

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return;

    if (!newState) {
      const { error } = await supabase
        .from("access_controls")
        .update({ is_disabled: true })
        .eq("owner_id", session.user.id)
        .eq("feature_name", feature);

      if (error) {
        toast.error("Erreur lors de la désactivation du code d'accès");
        return;
      }

      toast.success("Code d'accès désactivé");
    } else {
      onFeatureClick(feature);
    }
  };

  const featureLinks = ownerLinks.filter(link => 
    link.feature && 
    link.feature !== 'dashboard' &&
    hasFeatureAccess(link.feature)
  );

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div>
            <Label className="text-base font-semibold">
              Onglets nécessitant un code d'accès
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Activez ou désactivez le code d'accès pour chaque onglet :
            </p>
          </div>
          
          <div className="space-y-4">
            {featureLinks.map((link) => (
              <div key={link.name} className="flex items-center justify-between p-2 rounded-lg border">
                <div className="flex items-center gap-2">
                  <link.icon className="h-5 w-5 text-muted-foreground" />
                  <span>{link.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  {accessCodes[link.feature!] && (
                    <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                      {accessCodes[link.feature!]}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={enabledFeatures[link.feature!] || false}
                      onCheckedChange={() => handleToggleFeature(link.feature!)}
                    />
                    {enabledFeatures[link.feature!] && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onFeatureClick(link.feature!)}
                      >
                        {accessCodes[link.feature!] ? "Modifier" : "Définir"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};